/**
 * Shared matching of parsed P6 calendars against existing RDrive calendars.
 *
 * The RDrive calendar id is backend-generated, so imports need a stable key to
 * find "the same" calendar again. Two keys, in precedence order:
 *  1. the P6 ObjectId, persisted on the RDrive calendar as a `p6-id:<ObjectId>`
 *     tag at import time — survives renames in P6, but is specific to the
 *     source P6 database (a different instance may renumber ObjectIds),
 *  2. the deterministic calendar code (slug of the P6 Name, case-insensitive)
 *     — survives cross-instance re-exports, but not renames.
 *
 * Kept separate from XMLTaskParser so CalendarImporter can use it without
 * pulling in the store-dependent parser module.
 */
export const P6_ID_TAG_PREFIX = 'p6-id:';

/**
 * Tag persisting a P6 calendar ObjectId on an RDrive calendar.
 * @param {string} p6ObjectId
 * @returns {string} e.g. 'p6-id:6926'
 */
export function makeP6IdTag(p6ObjectId) {
  return `${P6_ID_TAG_PREFIX}${p6ObjectId}`;
}

/**
 * The P6 ObjectId stored on an RDrive calendar, if any.
 * @param {Object} calendar - RDrive CalendarDTO
 * @returns {string|null}
 */
export function getCalendarP6Id(calendar) {
  const tag = (calendar?.tags || []).find(t => typeof t === 'string' && t.startsWith(P6_ID_TAG_PREFIX));
  return tag ? tag.substring(P6_ID_TAG_PREFIX.length) : null;
}

/**
 * Find the existing RDrive calendar a parsed P6 calendar corresponds to:
 * p6-id tag first (rename-proof), then code (case-insensitive).
 *
 * @param {Object} p6Calendar - Parsed P6 calendar ({ p6ObjectId, code, ... })
 * @param {Array} calendars - Existing RDrive calendars
 * @returns {{ calendar: Object, matchedBy: 'p6Id'|'code' }|null}
 */
export function matchP6Calendar(p6Calendar, calendars) {
  if (!p6Calendar) return null;

  if (p6Calendar.p6ObjectId) {
    const byId = (calendars || []).find(c => getCalendarP6Id(c) === p6Calendar.p6ObjectId);
    if (byId) return { calendar: byId, matchedBy: 'p6Id' };
  }

  const codeLower = (p6Calendar.code || '').toLowerCase();
  if (codeLower) {
    const byCode = (calendars || []).find(c => (c.code || '').toLowerCase() === codeLower);
    if (byCode) return { calendar: byCode, matchedBy: 'code' };
  }

  return null;
}
